import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  AppDatePicker,
  SelectField,
  TextArea,
  TextField,
} from '../../common/InputField';
import Button from '../../common/Button';
import { useAppContext } from '../../../utils/context/app-context';
import { StatusValues, ToastType } from '../../../types/common.types';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import OrganisationsService from '../../../services/organisation.service';
import Switch from '../../common/Switch';
import AiChatModelService from '../../../services/ai-chat-models.service';
import { filter, includes, map } from 'lodash';
import { TAIChatModel } from '../../../types/chat-ai-models';
import Select from 'react-select';
import { TOrganisation } from '@answerr/types';

export const composioConnectors = [
  {
    id: '1',
    slug: 'GOOGLEDRIVE',
    authConfigId: 'ac_FudDM4f07X6f',
  },
  {
    id: '2',
    slug: 'SHARE_POINT',
    authConfigId: 'ac_YJooPKpGnyNP',
  },
  {
    id: '3',
    slug: 'HUBSPOT',
    authConfigId: 'ac_pVRU2hYHjMur',
  },
  {
    id: '4',
    slug: 'SALESFORCE',
    authConfigId: 'ac_j1EqkuWV40gn',
  },
  {
    id: '5',
    slug: 'CANVAS',
    authConfigId: '',
  },
  {
    id: '6',
    slug: 'd2lbrightspace',
    authConfigId: '',
  },
];

const lmsProviderOptions = [
  { value: 'canvas', label: 'Canvas' },
  { value: 'brightspace', label: 'D2L Brightspace' },
  { value: 'blackboard', label: 'Blackboard' },
];

const CreateOrganisatioinSchema = Yup.object().shape({
  organisationName: Yup.string()
    .required('Organisation Name is required.')
    .min(1)
    .max(100),
  primaryDomain: Yup.string()
    .matches(
      /((https?):\/\/)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Domain should be in proper format (https://www.yourdomain.com)'
    )
    .required('Primary Domain is required.')
    .min(1)
    .max(100),
  secondaryDomain: Yup.array().of(Yup.string()),
  numberOfCustomers: Yup.number().required('Number of Customers is required.'),
  fromDate: Yup.string().required('From Date is required'),
  toDate: Yup.string().required('To Date is required'),
  fileLimit: Yup.number().min(1).max(2000).default(10),
  fileSize: Yup.number().min(1).max(500).default(10),
  email: Yup.string().email('Invalid email').required('Required').trim(),
  password: Yup.string().min(8).max(50),
  isPlanActive: Yup.boolean().default(false),
  showUserChats: Yup.boolean().default(true),
  allowAiTutor: Yup.boolean().default(false),
  allowedAgents: Yup.number().default(0),
  allowedAssistants: Yup.number().default(0),
  scrappingLinksLimit: Yup.number().default(0),
  connectors: Yup.array()
    .of(
      Yup.object().shape({
        slug: Yup.string().required('Connector slug is required.'),
        allowedTools: Yup.array()
          .of(Yup.string().required('Tool is required.'))
          .required('Allowed tools are required.'),
      })
    )
    .default([]),
  allowAssistant: Yup.boolean().default(false),
  allowConnector: Yup.boolean().default(false),
  allowLMS: Yup.boolean().default(false),
  allowedLMSProviders: Yup.array()
    .of(Yup.string().oneOf(['canvas', 'brightspace', 'blackboard']))
    .when('allowLMS', {
      is: true,
      then: (schema) => schema.min(1, 'Select at least one LMS provider.'),
      otherwise: (schema) => schema.default([]),
    }),
  allowGrading: Yup.boolean().default(false),
  allowQuiz: Yup.boolean().default(false),
  allowAgent: Yup.boolean().default(false),
  showLLMConfig: Yup.boolean().default(false),
  allowTokenMgmt: Yup.boolean().default(false),
  monthlyTokenLimit: Yup.number().min(0).default(0),
  allowedModels: Yup.array().of(Yup.string()).default([]),
  allowAIQ: Yup.boolean().default(false),
  totalAIQSeats: Yup.number().min(0).default(0),
  allowACRA: Yup.boolean().default(false),
  totalACRASeats: Yup.number().min(0).default(0),
  minAiqCooldown: Yup.number().min(0).default(0),
  maxGenerationsPerUser: Yup.number().min(0).default(0),
  allowedAiqTypes: Yup.array().of(Yup.string()).default([]),
  status: Yup.string().required('Status is required.'),
});

const CreateOrganisation = () => {
  const { setToastInfo } = useAppContext();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [chatAIModels, setChatAIModels] = useState<TAIChatModel[]>([]);
  const [initialValues, setInitialValues] = useState<TOrganisation>({
    organisationName: '',
    primaryDomain: '',
    secondaryDomain: [],
    numberOfCustomers: 0,
    fromDate: '',
    toDate: '',
    aiModels: [],
    email: '',
    password: '',
    fileLimit: 10,
    fileSize: 10,
    makePublicAgents: false,
    isPlanActive: false,
    showUserChats: true,
    allowedAgents: 0,
    allowedAssistants: 0,
    scrappingLinksLimit: 0,
    connectors: [],
    disabledModels: [],
    allowCodeSnippet: false,
    allowAiTutor: false,
    allowAgent: false,
    allowAssistant: false,
    allowConnector: false,
    allowLMS: false,
    allowedLMSProviders: [],
    allowGrading: false,
    allowQuiz: false,
    showLLMConfig: false,
    allowAIQ: false,
    totalAIQSeats: 0,
    minAiqCooldown: 0,
    maxGenerationsPerUser: 0,
    allowedAiqTypes: [],
    allowACRA: false,
    totalACRASeats: 0,
    allowTokenMgmt: false,
    monthlyTokenLimit: 0,
    allowedModels: [],
    status: StatusValues.ACTIVE,
  });
  const [imagePreview, setImagePreview] = useState('');
  const imageRef = useRef<HTMLInputElement>(null);
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);

  const getActiveAIModels = async () => {
    try {
      const aiModelsResponse = await AiChatModelService.getAll(true);
      if (aiModelsResponse.data.data?.models) {
        setChatAIModels(aiModelsResponse.data.data.models);
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in fetching AI Models',
      });
    }
  };

  const getOrganisationById = async () => {
    try {
      const response = await OrganisationsService.getOrganisationById(id ?? '');
      if (response.data.data && response.data.data.organisation) {
        const organisation = response.data.data.organisation;
        setSelectedConnectors(map(organisation.connectors, 'slug'));
        setInitialValues({
          organisationName: organisation.organisationName,
          primaryDomain: organisation.primaryDomain,
          secondaryDomain: organisation.secondaryDomain,
          numberOfCustomers: organisation.numberOfCustomers,
          fromDate: organisation.fromDate,
          toDate: organisation.toDate,
          aiModels: organisation.aiModels,
          email: organisation.email?.trim(),
          password: organisation.password,
          fileLimit: organisation.fileLimit,
          fileSize: organisation.fileSize,
          makePublicAgents: organisation.makePublicAgents,
          isPlanActive: organisation.isPlanActive,
          showUserChats: organisation.showUserChats,
          allowedAgents: organisation.allowedAgents,
          allowedAssistants: organisation.allowedAssistants,
          scrappingLinksLimit: organisation.scrappingLinksLimit,
          connectors: organisation.connectors || [],
          allowCodeSnippet: organisation.allowCodeSnippet,
          allowAiTutor: organisation.allowAiTutor,
          status: organisation.status,
          disabledModels: organisation.disabledModels || [],
          allowAgent: organisation.allowAgent || false,
          allowAssistant: organisation.allowAssistant || false,
          allowConnector: organisation.allowConnector || false,
          allowLMS: organisation.allowLMS || false,
          allowedLMSProviders: organisation.allowedLMSProviders || [],
          allowGrading: organisation.allowGrading || false,
          allowQuiz: organisation.allowQuiz || false,
          showLLMConfig: organisation.showLLMConfig || false,
          allowAIQ: organisation.allowAIQ || false,
          totalAIQSeats: organisation.totalAIQSeats || 0,
          minAiqCooldown: organisation.minAiqCooldown || 0,
          maxGenerationsPerUser: organisation.maxGenerationsPerUser || 0,
          allowedAiqTypes: organisation.allowedAiqTypes || [],
          allowACRA: organisation.allowACRA || false,
          totalACRASeats: organisation.totalACRASeats || 0,
          allowTokenMgmt: organisation.allowTokenMgmt || false,
          monthlyTokenLimit: organisation.monthlyTokenLimit || 0,
          allowedModels: organisation.allowedModels || [],
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in fetching organisation.',
      });
    }
  };

  const saveOrganisation = async (values: TOrganisation) => {
    try {
      const response = await OrganisationsService.createOrganisation(values);
      if (response.data.data && response.data.data.organisation) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
        setInitialValues({
          organisationName: '',
          primaryDomain: '',
          secondaryDomain: [],
          numberOfCustomers: 0,
          fromDate: new Date(),
          toDate: new Date(),
          aiModels: [],
          email: '',
          password: '',
          fileLimit: 10,
          fileSize: 10,
          makePublicAgents: false,
          isPlanActive: false,
          status: StatusValues.ACTIVE,
          showUserChats: true,
          allowedAgents: 0,
          allowedAssistants: 0,
          scrappingLinksLimit: 0,
          allowCodeSnippet: false,
          allowAiTutor: false,
          connectors: [],
          disabledModels: [],
          allowAgent: false,
          allowAssistant: false,
          allowConnector: false,
          allowLMS: false,
          allowedLMSProviders: [],
          allowGrading: false,
          allowQuiz: false,
          showLLMConfig: false,
          allowAIQ: false,
          totalAIQSeats: 0,
          minAiqCooldown: 0,
          maxGenerationsPerUser: 0,
          allowACRA: false,
          totalACRASeats: 0,
          allowTokenMgmt: false,
          monthlyTokenLimit: 0,
          allowedModels: [],
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in creating organisation',
      });
    }
  };

  const updateOrganisation = async (values: TOrganisation) => {
    try {
      const response = await OrganisationsService.updateOrganisation({
        ...values,
        _id: id,
      });
      if (response.data.data && response.data.data.organisation) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
        const organisation = response.data.data.organisation;
        setInitialValues({
          organisationName: organisation.organisationName,
          primaryDomain: organisation.primaryDomain,
          secondaryDomain: organisation.secondaryDomain,
          numberOfCustomers: organisation.numberOfCustomers,
          fromDate: organisation.fromDate,
          toDate: organisation.toDate,
          aiModels: organisation.aiModels,
          email: organisation.email,
          password: organisation.password,
          fileLimit: organisation.fileLimit,
          fileSize: organisation.fileSize,
          makePublicAgents: organisation.makePublicAgents,
          isPlanActive: organisation.isPlanActive,
          status: organisation.status,
          showUserChats: organisation.showUserChats,
          allowedAgents: organisation.allowedAgents,
          allowedAssistants: organisation.allowedAssistants,
          scrappingLinksLimit: organisation.scrappingLinksLimit,
          allowCodeSnippet: organisation.allowCodeSnippet,
          allowAiTutor: organisation.allowAiTutor,
          connectors: organisation.connectors || [],
          disabledModels: organisation.disabledModels || [],
          allowAgent: organisation.allowAgent || false,
          allowAssistant: organisation.allowAssistant || false,
          allowConnector: organisation.allowConnector || false,
          allowLMS: organisation.allowLMS || false,
          allowedLMSProviders: organisation.allowedLMSProviders || [],
          allowGrading: organisation.allowGrading || false,
          allowQuiz: organisation.allowQuiz || false,
          showLLMConfig: organisation.showLLMConfig || false,
          allowAIQ: organisation.allowAIQ || false,
          totalAIQSeats: organisation.totalAIQSeats || 0,
          minAiqCooldown: organisation.minAiqCooldown || 0,
          maxGenerationsPerUser: organisation.maxGenerationsPerUser || 0,
          allowedAiqTypes: organisation.allowedAiqTypes || [],
          allowACRA: organisation.allowACRA || false,
          totalACRASeats: organisation.totalACRASeats || 0,
          allowTokenMgmt: organisation.allowTokenMgmt || false,
          monthlyTokenLimit: organisation.monthlyTokenLimit || 0,
          allowedModels: organisation.allowedModels || [],
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in updating plan',
      });
    }
  };

  const statusItems = [
    {
      title: 'Active',
      value: 'active',
    },
    {
      title: 'Inactive',
      value: 'inactive',
    },
    {
      title: 'Deleted',
      value: 'deleted',
    },
  ];

  const onFileChange = async () => {
    try {
      if (imageRef.current?.files) {
        console.log(imageRef.current?.files[0]);
        setLoading(true);
        const formData = new FormData();
        formData.append('img', imageRef?.current?.files[0]);
        const imageResponse = await OrganisationsService.updateImage(formData);
        if (imageResponse?.data?.data?.url) {
          setLoading(false);
          setToastInfo({
            isOpen: true,
            message: 'Image uploaded successfully',
            type: ToastType.SUCCESS,
          });
          setImagePreview('');
        }
      }
    } catch (err) {
      console.log('Error: ', err);
    }
  };

  const setUpdatedAIModels = () => {
    if (!chatAIModels.length) return;
    const aiChatModelNames = map(chatAIModels, 'aiModelName');
    const filteredModels = initialValues.aiModels.filter((model) =>
      includes(aiChatModelNames, model?.modelName)
    );
    const aiModelNames = map(initialValues.aiModels, 'modelName');
    const filteredChatModels = filter(
      chatAIModels,
      (model: TAIChatModel) => !includes(aiModelNames, model?.aiModelName)
    );
    const newAiModels = map(filteredChatModels, (model) => {
      return {
        platform: model.platform,
        modelName: model.aiModelName,
        inputTokens: 0,
        outputTokens: 0,
      };
    });
    setInitialValues({
      ...initialValues,
      aiModels: [...filteredModels, ...newAiModels],
    });
  };

  useEffect(() => {
    setUpdatedAIModels();
  }, [chatAIModels]);

  useEffect(() => {
    if (id) {
      getOrganisationById();
    }
  }, [id]);

  useEffect(() => {
    getActiveAIModels();
  }, []);

  return (
    <div className="w-full pb-10">
      <div className="border border-[rgba(0,0,0,.15)] rounded-3xl p-10">
        <Formik
          initialValues={initialValues}
          validationSchema={CreateOrganisatioinSchema}
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={(values, { resetForm }) => {
            if (id) {
              updateOrganisation({
                ...values,
                secondaryDomain: values.secondaryDomain,
              });
            } else {
              saveOrganisation({
                ...values,
                secondaryDomain: values.secondaryDomain,
              });
            }
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
            setFieldValue,
            dirty,
          }) => {
            return (
              <Form>
                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <TextField
                      name="organisationName"
                      label="Organisation Name"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.organisationName}
                      required={true}
                      isTouched={touched.organisationName}
                      value={values.organisationName}
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="primaryDomain"
                      label="Primary Domain"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.primaryDomain}
                      required={true}
                      isTouched={touched.primaryDomain}
                      value={values.primaryDomain}
                      disabled={id ? true : false}
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="numberOfCustomers"
                      label="Number of Customers"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.numberOfCustomers}
                      required={true}
                      isTouched={touched.numberOfCustomers}
                      value={values.numberOfCustomers}
                      type="number"
                    />
                  </div>
                </div>
                <div className="mb-5">
                  <TextArea
                    name="secondaryDomain"
                    label="Secondary Domain"
                    placeholder=""
                    onChange={(e) =>
                      setFieldValue(
                        'secondaryDomain',
                        e.target.value.split(',').map((item) => item.trim())
                      )
                    }
                    onBlur={(e) =>
                      setFieldValue(
                        'secondaryDomain',
                        e.target.value.split(',').map((item) => item.trim())
                      )
                    }
                    error={errors.secondaryDomain}
                    isTouched={touched.secondaryDomain}
                    value={values.secondaryDomain?.join(', ')}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <AppDatePicker
                      name="fromDate"
                      onChange={(date) => setFieldValue('fromDate', date)}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      maxDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 1)
                        )
                      }
                      minDate={new Date()}
                      startDate={new Date()}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      placeholder="From Date"
                      label="From Date"
                      value={values.fromDate as Date}
                      error={errors.fromDate}
                    />
                  </div>

                  <div className="mb-5">
                    <AppDatePicker
                      name="toDate"
                      onChange={(date) => setFieldValue('toDate', date)}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      maxDate={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 3)
                        )
                      }
                      minDate={(values.fromDate as Date) ?? new Date()}
                      startDate={values.fromDate as Date}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      placeholder="To Date"
                      label="To Date"
                      value={values.toDate as Date}
                      error={errors.toDate}
                    />
                  </div>

                  <div className="mb-5">
                    <TextField
                      name="email"
                      label="Email"
                      placeholder="Type Email..."
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.email}
                      required={true}
                      isTouched={touched.email}
                      value={values.email}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <SelectField
                      name="status"
                      label="Status"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.status}
                      isTouched={touched.status}
                      value={values.status}
                      items={statusItems}
                    />
                  </div>

                  <div className="mb-5">
                    <TextField
                      name="fileLimit"
                      label="File Limit"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.fileLimit}
                      required={true}
                      isTouched={touched.fileLimit}
                      value={values.fileLimit}
                      type="number"
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="fileSize"
                      label="File Size Limit (MB)"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.fileSize}
                      required={true}
                      isTouched={touched.fileSize}
                      value={values.fileSize}
                      type="number"
                    />
                  </div>
                  {/* <div className="mb-5">
                    <input
                      accept="image/*"
                      id="contained-button-file"
                      multiple
                      type="file"
                      ref={imageRef}
                      hidden
                      onChange={(e) => {
                        onFileChange();
                        const fileReader = new FileReader();
                        fileReader.onload = () => {
                          if (fileReader.readyState === 2) {
                            setImagePreview(fileReader.result as string);
                          }
                        };
                        if (imageRef.current?.files)
                        fileReader.readAsDataURL(imageRef.current?.files[0]);
                      }}
                    ></input>
                    <button
                      className="text-[#333] dark:text-[#bdbdbd] mt-1 underline"
                      onClick={() => imageRef.current?.click()}
                    >
                      {values?.companyLogo ? 'Update ' : 'Upload '} Image
                    </button>
                    {
                      imagePreview && <img src={imagePreview} height={80} width={80}/>
                    }
                  </div> */}
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Allow Code Snippet"
                      handleClick={() => {
                        setFieldValue(
                          'allowCodeSnippet',
                          !values.allowCodeSnippet
                        );
                      }}
                      active={values.allowCodeSnippet}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Agent"
                      handleClick={() => {
                        setFieldValue('allowAgent', !values.allowAgent);
                      }}
                      active={Boolean(values.allowAgent)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Is Plan Active"
                      handleClick={() => {
                        setFieldValue('isPlanActive', !values.isPlanActive);
                      }}
                      active={values.isPlanActive}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Public Agents"
                      handleClick={() => {
                        setFieldValue(
                          'makePublicAgents',
                          !values.makePublicAgents
                        );
                      }}
                      active={values.makePublicAgents}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Allow Assistant"
                      handleClick={() => {
                        setFieldValue('allowAssistant', !values.allowAssistant);
                      }}
                      active={Boolean(values.allowAssistant)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Connectors"
                      handleClick={() => {
                        setFieldValue('allowConnector', !values.allowConnector);
                      }}
                      active={Boolean(values.allowConnector)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow LMS"
                      handleClick={() => {
                        const nextValue = !values.allowLMS;
                        setFieldValue('allowLMS', nextValue);
                        if (!nextValue) {
                          setFieldValue('allowedLMSProviders', []);
                        }
                      }}
                      active={Boolean(values.allowLMS)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Grading"
                      handleClick={() => {
                        setFieldValue('allowGrading', !values.allowGrading);
                      }}
                      active={Boolean(values.allowGrading)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Ai Tutor"
                      handleClick={() => {
                        setFieldValue('allowAiTutor', !values.allowAiTutor);
                      }}
                      active={Boolean(values.allowAiTutor)}
                    />
                  </div>
                </div>

                {values.allowLMS && (
                  <div className="mb-5 w-3/4">
                    <label className="mb-1 block text-[14px] text-[#666]">
                      Active LMS Providers
                    </label>
                    <Select
                      options={lmsProviderOptions}
                      value={lmsProviderOptions.filter((option) =>
                        (values.allowedLMSProviders as string[] | undefined)?.includes(
                          option.value
                        )
                      )}
                      isMulti
                      placeholder="Select Canvas, D2L Brightspace, or Blackboard"
                      onChange={(selectedOptions) =>
                        setFieldValue(
                          'allowedLMSProviders',
                          selectedOptions.map((option) => option.value)
                        )
                      }
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderRadius: '6px',
                          background: 'transparent',
                          borderColor: state.isFocused ? '#020817' : '#ccc',
                          boxShadow: 'none',
                          '&:hover': { borderColor: '#020817' },
                        }),
                      }}
                    />
                    {errors.allowedLMSProviders &&
                      touched.allowedLMSProviders && (
                        <p className="mt-1 text-[12px] text-red-500">
                          {String(errors.allowedLMSProviders)}
                        </p>
                      )}
                  </div>
                )}

                <div className="grid grid-cols-4 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Allow Quiz"
                      handleClick={() => {
                        setFieldValue('allowQuiz', !values.allowQuiz);
                      }}
                      active={Boolean(values.allowQuiz)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Show User Chats"
                      handleClick={() => {
                        setFieldValue('showUserChats', !values.showUserChats);
                      }}
                      active={values.showUserChats}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Allow Image Extraction"
                      handleClick={() => {
                        setFieldValue('extractImages', !values.extractImages);
                      }}
                      active={Boolean(values.extractImages)}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Show LLM Config"
                      handleClick={() => {
                        setFieldValue('showLLMConfig', !values.showLLMConfig);
                      }}
                      active={Boolean(values.showLLMConfig)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="mb-5 w-3/4">
                    <label className="block h-full mb-1 sm:text-[14px] text-[#666]">
                      Allowed Connectors
                    </label>
                    <Select
                      options={composioConnectors}
                      value={composioConnectors.filter((option) =>
                        selectedConnectors.includes(option.slug)
                      )}
                      getOptionLabel={(option) => `${option.slug}`}
                      getOptionValue={(option) => `${option.slug}`}
                      isMulti={true}
                      onChange={(selectedOptions) => {
                        setSelectedConnectors(map(selectedOptions, 'slug'));
                        setFieldValue(
                          'connectors',
                          map(selectedOptions, (option) => {
                            return {
                              slug: option.slug,
                              allowedTools: [],
                            };
                          })
                        );
                      }}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderRadius: '6px',
                          background: 'transparent',
                          borderColor: state.isFocused ? '#020817' : '#ccc',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': { borderColor: '#020817' },
                        }),
                      }}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Show AIQ"
                      handleClick={() => {
                        setFieldValue('allowAIQ', !values.allowAIQ);
                      }}
                      active={Boolean(values.allowAIQ)}
                    />
                  </div>
                </div>

                {values.allowAIQ && (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="mb-5">
                        <TextField
                          name="totalAIQSeats"
                          label="Total AIQ Seats"
                          placeholder=""
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.totalAIQSeats}
                          isTouched={touched.totalAIQSeats}
                          value={values.totalAIQSeats}
                          type="number"
                        />
                      </div>
                      <div className="mb-5">
                        <TextField
                          name="minAiqCooldown"
                          label="Min AIQ Cooldown (days)"
                          placeholder=""
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.minAiqCooldown}
                          isTouched={touched.minAiqCooldown}
                          value={values.minAiqCooldown}
                          type="number"
                        />
                      </div>
                      <div className="mb-5">
                        <TextField
                          name="maxGenerationsPerUser"
                          label="Max Generations Per User"
                          placeholder=""
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors.maxGenerationsPerUser}
                          isTouched={touched.maxGenerationsPerUser}
                          value={values.maxGenerationsPerUser}
                          type="number"
                        />
                      </div>
                    </div>
                    <div className="mb-5">
                      <label className="block mb-1 sm:text-[14px] text-[#666]">
                        Allowed AIQ Types
                      </label>
                      <Select
                        options={[
                          { label: 'Learner', value: 'learner' },
                          { label: 'Certified', value: 'certified' },
                        ]}
                        value={[
                          { label: 'Learner', value: 'learner' },
                          { label: 'Certified', value: 'certified' },
                        ].filter((opt) =>
                          (values.allowedAiqTypes || []).includes(opt.value)
                        )}
                        isMulti={true}
                        onChange={(selectedOptions) => {
                          setFieldValue(
                            'allowedAiqTypes',
                            map(selectedOptions, 'value')
                          );
                        }}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderRadius: '6px',
                            background: 'transparent',
                            borderColor: state.isFocused ? '#020817' : '#ccc',
                            boxShadow: state.isFocused ? 'none' : 'none',
                            '&:hover': { borderColor: '#020817' },
                          }),
                        }}
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Allow ACRA"
                      handleClick={() => {
                        setFieldValue('allowACRA', !values.allowACRA);
                      }}
                      active={Boolean(values.allowACRA)}
                    />
                  </div>
                  {values.allowACRA && (
                    <div className="mb-5">
                      <TextField
                        name="totalACRASeats"
                        label="Total ACRA Seats"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.totalACRASeats}
                        isTouched={touched.totalACRASeats}
                        value={values.totalACRASeats}
                        type="number"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <TextField
                      name="allowedAgents"
                      label="Allowed Agents"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.allowedAgents}
                      required={true}
                      isTouched={touched.allowedAgents}
                      value={values.allowedAgents}
                      type="number"
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="allowedAssistants"
                      label="Allowed Assistants"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.allowedAssistants}
                      required={true}
                      isTouched={touched.allowedAssistants}
                      value={values.allowedAssistants}
                      type="number"
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="scrappingLinksLimit"
                      label="Scrapping Links Limit"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.scrappingLinksLimit}
                      required={true}
                      isTouched={touched.scrappingLinksLimit}
                      value={values.scrappingLinksLimit}
                      type="number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Allow Token Management"
                      handleClick={() => {
                        setFieldValue('allowTokenMgmt', !values.allowTokenMgmt);
                      }}
                      active={Boolean(values.allowTokenMgmt)}
                    />
                  </div>
                  <div className="mb-5">
                    <TextField
                      name="monthlyTokenLimit"
                      label="Monthly Token Limit"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.monthlyTokenLimit}
                      required={true}
                      isTouched={touched.monthlyTokenLimit}
                      value={values.monthlyTokenLimit}
                      type="number"
                      disabled={!values.allowTokenMgmt}
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block mb-3 sm:text-[14px] text-[#666]">
                    Allowed Models
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {map(chatAIModels, (model) => {
                      const isAllowed = includes(
                        values.allowedModels,
                        model.aiModelName
                      );
                      return (
                        <div className="mb-2" key={model.aiModelName}>
                          <Switch
                            label={model.publicName || model.aiModelName}
                            handleClick={() => {
                              setFieldValue(
                                'allowedModels',
                                isAllowed
                                  ? filter(
                                      values.allowedModels,
                                      (modelName) =>
                                        modelName !== model.aiModelName
                                    )
                                  : [
                                      ...(values.allowedModels || []),
                                      model.aiModelName,
                                    ]
                              );
                            }}
                            active={isAllowed}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* <div>
                  <FieldArray
                    name="aiModels"
                    render={(arrayHelpers) => {
                      return map(values.aiModels, (model, index) => (
                        <div className="grid grid-flow-col gap-4" key={index}>
                          <div className="mb-5">
                            <TextField
                              name={`aiModels[${index}].modelName`}
                              label="Model Name"
                              placeholder=""
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={true}
                              disabled={true}
                              value={values.aiModels[index]?.modelName}
                              isTouched={
                                touched.aiModels &&
                                touched.aiModels[index]?.modelName
                              }
                            />
                          </div>
                          <div className="mb-5">
                            <TextField
                              name={`aiModels[${index}].inputTokens`}
                              label="Input Token Limit"
                              type="number"
                              placeholder=""
                              onChange={handleChange}
                              onBlur={handleBlur}
                              // error={errors.description}
                              isTouched={
                                touched.aiModels &&
                                touched.aiModels[index]?.inputTokens
                              }
                              value={values.aiModels[index]?.inputTokens}
                            />
                          </div>
                          <div className="mb-5">
                            <TextField
                              name={`aiModels[${index}].outputTokens`}
                              label="Output Token Limit"
                              type="number"
                              placeholder=""
                              onChange={handleChange}
                              onBlur={handleBlur}
                              // error={
                              //   errors.aiModels &&
                              //   errors.aiModels[index] &&
                              //   errors.aiModels[index].outputTokens
                              // }
                              isTouched={
                                touched.aiModels &&
                                touched.aiModels[index]?.outputTokens
                              }
                              value={values.aiModels[index]?.outputTokens}
                            />
                          </div>
                        </div>
                      ));
                    }}
                  />
                </div> */}

                <div className="text-center">
                  <Button
                    type="button"
                    disabled={!touched || !isValid || !dirty}
                    onClick={() => handleSubmit()}
                  >
                    {id ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CreateOrganisation;
